import React, { createContext, useContext, useMemo } from 'react';
import {
  BannerPropTypes,
  TitlePropTypes,
  TitleDefaultProps,
  ToggleRowPropTypes,
  ToggleRowDefaultProps,
  MetricsPropTypes,
  MetricsDefaultProps,
  TeamAlertPropTypes,
  SummaryPropTypes,
  SummaryDefaultProps,
} from './Summary.types';
import * as Styles from './Summary.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { format } from 'date-fns';
import bannerCriticalPath from '../../media/banner-critical.svg';
import bannerSuccessPath from '../../media/banner-success.svg';
import bannerWarningPath from '../../media/banner-warning.svg';
import bannerGradientPath from '../../media/banner-gradient.svg';

import {
  AlertV2,
  IconWarning,
  Skeleton,
  Toggle,
  Tooltip,
} from 'gatorcomponents';

const SummaryContext = createContext({ content: {}, loading: false });
const useSummaryContext = () => useContext(SummaryContext);

const BANNER_CONFIG = {
  trialActive: {
    icon: bannerSuccessPath,
    bannerType: 'success',
    alt: 'Trial Active',
    testId: 'trial-active-message',
  },
  cleaningInProgress: {
    icon: bannerWarningPath,
    bannerType: 'warning',
    alt: 'Cleaning in Progress',
    testId: 'cleaning-in-progress-message',
  },
  limitedProtection: {
    icon: bannerWarningPath,
    bannerType: 'warning',
    alt: 'Limited Protection',
    testId: 'limited-protection-message',
  },
  fullProtectionActive: {
    icon: bannerGradientPath,
    bannerType: 'success-gradient',
    alt: 'Full Protection Active',
    testId: 'full-protection-active-message',
  },
  expiredProtection: {
    icon: bannerCriticalPath,
    bannerType: 'critical',
    alt: 'Expired Protection',
    testId: 'expired-protection-message',
  },
};

const Banner = ({ type, trialDaysLeft }) => {
  const { content } = useSummaryContext();
  const config = BANNER_CONFIG[type];
  if (!config) return null;

  const contentMap = {
    trialActive: content.alerts.trialActive(trialDaysLeft || 0),
    cleaningInProgress: content.alerts.cleaningInProgress,
    limitedProtection: content.alerts.limitedProtection,
    fullProtectionActive: content.alerts.fullProtectionActive,
    expiredProtection: content.alerts.expiredProtection,
  };

  const isFullProtection = type === 'fullProtectionActive';

  return (
    <Styles.StatusBanner type={config.bannerType} data-testid={config.testId}>
      <Styles.StatusIcon src={config.icon} alt={config.alt} />
      {isFullProtection ? (
        <Styles.FullProtectionBannerContent>
          <Styles.FullProtectionBannerText>{contentMap[type]}</Styles.FullProtectionBannerText>
        </Styles.FullProtectionBannerContent>
      ) : (
        contentMap[type]
      )}
    </Styles.StatusBanner>
  );
};

const Title = ({
  loading = false,
}) => {
  const { content } = useSummaryContext();

  if (loading) {
    return (
      <Styles.TitleSkeletonWrapper>
        <Skeleton height="100%" width="240px" />
      </Styles.TitleSkeletonWrapper>
    );
  }

  return <Styles.Title>{content.title}</Styles.Title>;
};

export const ToggleRow = ({
  enabled,
  onToggle,
  lastScan,
  loading,
}) => {
  const { content } = useSummaryContext();

  if (loading) {
    return (
      <Skeleton width="440px" height="40px" testId="summary-toggle-row-skeleton" />
    );
  }

  if (enabled === undefined || typeof onToggle !== 'function' || !lastScan) return null;

  const lastScanData = () => lastScan && (
    <Styles.ToggleColumn>
      <Styles.Label>
        {content.automaticRemovalToggle.automaticRemovalLabel(enabled)}
      </Styles.Label>

      <Styles.LabelTime>
        {lastScan && content.automaticRemovalToggle.lastVerification({
          hour: lastScan ? format(new Date(lastScan), 'HH\'h\'mm') : '--',
          day: lastScan ? format(new Date(lastScan), 'dd/MM/yyyy') : '--',
        })}
      </Styles.LabelTime>
    </Styles.ToggleColumn>
  );

  return (
    <Styles.TitleWithToggleRow>
      <Styles.AutomaticRemovalRow data-testid="AutomaticRemovalRow">
        <Tooltip
          content={content.automaticRemovalToggle.automaticRemovalTooltip}
          preferredPosition="topLeft"
        >
          <Styles.ToggleLabel onClick={onToggle}>
            <Toggle
              testId="automatic-cleaning-toggle"
              checked={enabled}
              controlledByFather
            />

            {lastScanData()}
          </Styles.ToggleLabel>
        </Tooltip>
      </Styles.AutomaticRemovalRow>
    </Styles.TitleWithToggleRow>
  );
};

const Metrics = ({
  threatsIdentified,
  threatsRemediated,
  onToggle,
  enabled,
  lastScan,
  hasToggle,
}) => {
  const { content, loading } = useSummaryContext();

  const memoizedMetrics = useMemo(() => {
    const showMetrics = (!!lastScan || loading);
    const showNotScannedMessage = !lastScan && !loading;
    const showLastScanData = !!lastScan && !loading && !hasToggle;

    return (
      <Styles.Content>
        {showMetrics && (
          <Styles.Row>
            <Styles.Column>
              <Styles.ColumnTextWrapper>
                <Styles.ColumnTitle>{content.threatsIdentified}</Styles.ColumnTitle>

                <Styles.ColumnValue data-testid="identified">
                  {loading ? <Skeleton height="40px" width="80px" /> : (threatsIdentified ?? 0)}
                </Styles.ColumnValue>
              </Styles.ColumnTextWrapper>
            </Styles.Column>

            <Styles.Column>
              <Styles.Divider />
            </Styles.Column>

            <Styles.Column>
              <Styles.ColumnTextWrapper>
                <Styles.ColumnTitle>
                  {content.threatsRemediated}

                  {!enabled && (
                    <Styles.ColumnIcon>
                      <IconWarning color="problemPure" />
                    </Styles.ColumnIcon>
                  )}
                </Styles.ColumnTitle>

                <Styles.ColumnValue
                  data-testid="remediated"
                  $grey={(threatsIdentified>threatsRemediated) || !enabled}
                  $green={threatsRemediated > 0}
                >
                  {loading
                    ? <Skeleton height="40px" width="80px" />
                    : (threatsRemediated ?? 0)
                  }

                  <Styles.EnableYourProtection>
                    {content.enableYourProtection}
                  </Styles.EnableYourProtection>
                </Styles.ColumnValue>
              </Styles.ColumnTextWrapper>
            </Styles.Column>
          </Styles.Row>
        )}

        {showNotScannedMessage && (
          <Styles.WithoutLastScanRow>
            <Styles.LastScanTitle>{content.withoutLastScan.title}</Styles.LastScanTitle>
            <Styles.LastScanDescription>{content.withoutLastScan.description}</Styles.LastScanDescription>
          </Styles.WithoutLastScanRow>
        )}

        {showLastScanData && (
          <Styles.LastScanInfo>
            <Styles.LastScanData>
              {content.automaticRemovalToggle.lastVerification({
                hour: lastScan ? format(new Date(lastScan), 'HH\'h\'mm') : '--',
                day: lastScan ? format(new Date(lastScan), 'dd/MM/yyyy') : '--',
              })}
            </Styles.LastScanData>
          </Styles.LastScanInfo>
        )}

        <ToggleRow
          enabled={enabled}
          onToggle={onToggle}
          lastScan={lastScan}
          hasToggle={hasToggle}
          loading={loading}
        />
      </Styles.Content>
    );
  }, [threatsIdentified, threatsRemediated, onToggle, enabled, lastScan, hasToggle, content, loading]);

  return memoizedMetrics;
};

const TeamAlert = ({ variant, threatsNumber, onOpenTicket }) => {
  const { content } = useSummaryContext();

  if (variant === 'intervention') {
    return (
      <AlertV2
        title={content.alerts.teamIntervention.title(threatsNumber)}
        description={content.alerts.teamIntervention.description}
        primaryButtonLabel={content.alerts.teamIntervention.buttonLabel}
        onPrimaryAction={onOpenTicket}
        variant="critical"
      />
    );
  }

  return (
    <AlertV2
      title={content.alerts.teamInterventionResolved.title}
      description={content.alerts.teamInterventionResolved.description}
      primaryButtonLabel={content.alerts.teamInterventionResolved.buttonLabel}
      onPrimaryAction={onOpenTicket}
      variant="success"
    />
  );
};

const Banners = ({ children }) => <>{children}</>;

const Main = ({ children, testId }) => (
  <Styles.Container data-testid={testId}>
    <Styles.Adapter>
      <Styles.TitleAndColumnsContainer>
        {children}
      </Styles.TitleAndColumnsContainer>
    </Styles.Adapter>
  </Styles.Container>
);

const Alerts = ({ children }) => <>{children}</>;

const Summary = ({
  content,
  loading,
  testId,
  children,
}) => (
  <SummaryContext.Provider value={{ content, loading }}>
    {children({
      Banners,
      Main: function MainWithTestId(props) {
        return <Main testId={testId} {...props} />;
      },
      Alerts,
    })}
  </SummaryContext.Provider>
);

const SummaryWithErrorBoundary = withErrorBoundary(Summary, 'Summary');

SummaryWithErrorBoundary.Banner = Banner;
SummaryWithErrorBoundary.Title = Title;
SummaryWithErrorBoundary.Metrics = Metrics;
SummaryWithErrorBoundary.TeamAlert = TeamAlert;
SummaryWithErrorBoundary.ToggleRow = ToggleRow;


Banner.propTypes = BannerPropTypes;
Title.propTypes = TitlePropTypes;
Title.defaultProps = TitleDefaultProps;
ToggleRow.propTypes = ToggleRowPropTypes;
ToggleRow.defaultProps = ToggleRowDefaultProps;
Metrics.propTypes = MetricsPropTypes;
Metrics.defaultProps = MetricsDefaultProps;
TeamAlert.propTypes = TeamAlertPropTypes;
Summary.propTypes = SummaryPropTypes;
Summary.defaultProps = SummaryDefaultProps;

export default SummaryWithErrorBoundary;
